import React, { useEffect, lazy, Suspense, useRef, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SubscriptionProvider, useSubscription } from './contexts/SubscriptionContext';
import { LoadingProvider, useLoading } from './contexts/LoadingContext';
import { AdminAuthProvider, useAdminAuth } from './contexts/AdminAuthContext';
import SubscriptionGuard from './components/SubscriptionGuard';

import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Layout from './components/Layout';
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { App as CapacitorApp } from '@capacitor/app';
import { PushNotifications } from '@capacitor/push-notifications';
import LoadingScreen from './components/LoadingScreen';
import api from './utils/api';

// Lazy load pages for better initial load performance
const Home = lazy(() => import('./pages/Home'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const VerifyEmail = lazy(() => import('./pages/VerifyEmail'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Hospitals = lazy(() => import('./pages/Hospitals'));
const AddHospital = lazy(() => import('./pages/AddHospital'));
const EditHospital = lazy(() => import('./pages/EditHospital'));
const AddCategory = lazy(() => import('./pages/AddCategory'));
const Visits = lazy(() => import('./pages/Visits'));
const AddVisit = lazy(() => import('./pages/AddVisit'));
const EditVisit = lazy(() => import('./pages/EditVisit'));
const Reports = lazy(() => import('./pages/Reports'));
const PaymentDues = lazy(() => import('./pages/PaymentDues'));
const MonthlyPaymentDues = lazy(() => import('./pages/MonthlyPaymentDues'));
const Profile = lazy(() => import('./pages/Profile'));
const Subscription = lazy(() => import('./pages/Subscription'));
const Payment = lazy(() => import('./pages/Payment'));
const Help = lazy(() => import('./pages/Help'));
const About = lazy(() => import('./pages/About'));
const Notifications = lazy(() => import('./pages/Notifications'));
const AdminLogin = lazy(() => import('./pages/AdminLogin'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));

// Loading component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
  </div>
);

// Protected Route component
const ProtectedRoute = ({ children, requireSubscription = true }) => {
  const { isAuthenticated, loading, user } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  // Email verification gate (Profile is allowed to avoid redirect loops).
  if (!user?.isEmailVerified && location.pathname !== '/profile') {
    return <Navigate to="/profile" replace state={{ reason: 'email_verification_required' }} />;
  }

  // If subscription is required, wrap with subscription guard
  if (requireSubscription) {
    // Never require subscription gating for the email/profile flow itself.
    if (location.pathname === '/profile') return children;
    return <SubscriptionGuard>{children}</SubscriptionGuard>;
  }
  
  return children;
};

// Public Route component (redirect if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    if (user?.isEmailVerified) {
      return <Navigate to="/dashboard" replace />;
    }
    if (location.pathname === '/register') {
      return children;
    }
    return <Navigate to="/profile" replace />;
  }

  return children;
};

const AdminProtectedRoute = ({ children }) => {
  const { isAdminAuthenticated } = useAdminAuth();
  if (!isAdminAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }
  return children;
};

// Back Button Handler Component (needs to be inside Router)
function BackButtonHandler() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    const handleBackButton = async ({ canGoBack }) => {
      // First, check if chat widget wants to handle it
      const chatHandled = new Promise((resolve) => {
        const timeout = setTimeout(() => resolve(false), 100);
        const handler = () => {
          clearTimeout(timeout);
          window.removeEventListener('meera:back-handled', handler);
          resolve(true);
        };
        window.addEventListener('meera:back-handled', handler, { once: true });
        
        // Dispatch event for chat widget
        const ev = new CustomEvent('meera:back');
        window.dispatchEvent(ev);
        
        // If chat doesn't handle it within 100ms, proceed with navigation
        setTimeout(() => {
          clearTimeout(timeout);
          window.removeEventListener('meera:back-handled', handler);
          resolve(false);
        }, 100);
      });

      const chatConsumed = await chatHandled;
      if (chatConsumed) {
        return; // Chat widget handled it
      }

      // Get current path
      const currentPath = location.pathname;
      
      // If user is not authenticated, allow default behavior (go to login or close)
      if (!isAuthenticated) {
        // If on login/register pages, allow app to close
        if (currentPath === '/login' || currentPath === '/register' || currentPath === '/verify-email') {
          if (window.confirm('Do you want to close the app?')) {
            CapacitorApp.exitApp();
          }
        }
        return;
      }
      
      // If not on dashboard, navigate to dashboard
      if (currentPath !== '/dashboard' && currentPath !== '/') {
        navigate('/dashboard');
        return;
      }

      // If on dashboard, ask for confirmation to close app
      if (currentPath === '/dashboard' || currentPath === '/') {
        if (window.confirm('Do you want to close the app?')) {
          CapacitorApp.exitApp();
        }
      }
    };

    const backListener = CapacitorApp.addListener('backButton', handleBackButton);

    return () => {
      if (backListener) backListener.remove();
    };
  }, [location.pathname, navigate, isAuthenticated]);

  return null;
}

function NotificationBootstrap() {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const initializedRef = useRef(false);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    if (!isAuthenticated) return;
    if (initializedRef.current) return;
    initializedRef.current = true;

    let registrationSub = null;
    let registrationErrorSub = null;
    let actionSub = null;

    const setupPush = async () => {
      try {
        let permStatus = await PushNotifications.checkPermissions();
        if (permStatus.receive === 'prompt') {
          permStatus = await PushNotifications.requestPermissions();
        }
        if (permStatus.receive !== 'granted') return;

        registrationSub = await PushNotifications.addListener('registration', async (token) => {
          try {
            await api.post('/notifications/register-token', {
              token: token.value,
              platform: Capacitor.getPlatform(),
              appVersion: process.env.REACT_APP_VERSION || 'unknown',
            });
          } catch {
            // keep silent - API can be retried next login
          }
        });

        registrationErrorSub = await PushNotifications.addListener('registrationError', (error) => {
          console.error('Push registration error:', error);
        });

        actionSub = await PushNotifications.addListener('pushNotificationActionPerformed', (event) => {
          const deepLink = String(event?.notification?.data?.deepLink || '').trim();
          if (deepLink && deepLink.startsWith('/')) {
            navigate(deepLink);
          }
        });

        await PushNotifications.register();
      } catch (e) {
        console.error('Push setup failed:', e);
      }
    };

    setupPush();

    return () => {
      registrationSub?.remove?.();
      registrationErrorSub?.remove?.();
      actionSub?.remove?.();
      initializedRef.current = false;
    };
  }, [isAuthenticated, navigate]);

  return null;
}

// Inner component that has access to AuthContext
function AppContent() {
  const { isAuthenticated, loading: authLoading, user } = useAuth();
  const { isLoading: subscriptionLoading } = useSubscription();
  const { isPageLoading, loadingProgress, loadingMessage, setLoading } = useLoading();
  const [initialLoadingProgress, setInitialLoadingProgress] = useState(0);

  // Drive progress from auth + subscription; feature tips rotate in LoadingScreen.
  useEffect(() => {
    let progress = 0;

    if (authLoading) {
      progress = 30;
    } else if (isAuthenticated && subscriptionLoading) {
      progress = 60;
    } else if (isAuthenticated && !subscriptionLoading) {
      progress = 90;
    } else if (!authLoading && !isAuthenticated) {
      progress = 100;
    }

    setInitialLoadingProgress(progress);
  }, [authLoading, isAuthenticated, subscriptionLoading]);

  useEffect(() => {
    if (!isAuthenticated || !user?.id) return;

    const key = `heartbeat_last_sent_${user.id}`;
    const now = Date.now();
    const lastSent = Number(localStorage.getItem(key) || '0');
    // Throttle heartbeat writes to once every 6 hours.
    if (now - lastSent < 6 * 60 * 60 * 1000) return;

    api.post('/notifications/heartbeat').catch(() => {});
    localStorage.setItem(key, String(now));
  }, [isAuthenticated, user?.id]);

  // Show loading screen if auth or subscription is loading (initial load)
  const [loadingComplete, setLoadingComplete] = useState(false);
  const showInitialLoading = (authLoading || (isAuthenticated && subscriptionLoading)) && !loadingComplete;
  
  // Show loading screen for page/data loading
  const showPageLoading = isPageLoading && !showInitialLoading;

  return (
    <>
      {showInitialLoading && (
        <LoadingScreen 
          progress={initialLoadingProgress} 
          message="Loading data..." 
          onComplete={() => setLoadingComplete(true)}
        />
      )}
      {showPageLoading && (
        <LoadingScreen 
          progress={loadingProgress} 
          message={loadingMessage} 
          onComplete={() => setLoading(false)}
        />
      )}
      {!showInitialLoading && <AppRoutes />}
      <ToastContainer position="top-right" autoClose={1000} hideProgressBar theme="colored" />
    </>
  );
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes - No Auth Required */}
      <Route
        path="/"
        element={
          <Suspense fallback={<PageLoader />}>
            <Home />
          </Suspense>
        }
      />
      <Route
        path="/admin/login"
        element={
          <Suspense fallback={<PageLoader />}>
            <AdminLogin />
          </Suspense>
        }
      />
      <Route
        path="/admin"
        element={
          <AdminProtectedRoute>
            <Suspense fallback={<PageLoader />}>
              <AdminDashboard />
            </Suspense>
          </AdminProtectedRoute>
        }
      />
      <Route
        path="/home"
        element={
          <Suspense fallback={<PageLoader />}>
            <Home />
          </Suspense>
        }
      />
      <Route
        path="/privacy-policy"
        element={
          <Suspense fallback={<PageLoader />}>
            <PrivacyPolicy />
          </Suspense>
        }
      />
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Suspense fallback={<PageLoader />}>
              <Login />
            </Suspense>
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <Suspense fallback={<PageLoader />}>
              <Register />
            </Suspense>
          </PublicRoute>
        }
      />
      <Route
        path="/verify-email"
        element={
          <PublicRoute>
            <Suspense fallback={<PageLoader />}>
              <VerifyEmail />
            </Suspense>
          </PublicRoute>
        }
      />

      {/* Protected Routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Layout>
              <Suspense fallback={<PageLoader />}>
                <Dashboard />
              </Suspense>
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/hospitals"
        element={
          <ProtectedRoute>
            <Layout>
              <Suspense fallback={<PageLoader />}>
                <Hospitals />
              </Suspense>
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/hospitals/add"
        element={
          <ProtectedRoute>
            <Layout>
              <Suspense fallback={<PageLoader />}>
                <AddHospital />
              </Suspense>
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/hospitals/:id/edit"
        element={
          <ProtectedRoute>
            <Layout>
              <Suspense fallback={<PageLoader />}>
                <EditHospital />
              </Suspense>
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/categories"
        element={
          <ProtectedRoute>
            <Layout>
              <Suspense fallback={<PageLoader />}>
                <AddCategory />
              </Suspense>
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/visits"
        element={
          <ProtectedRoute>
            <Layout>
              <Suspense fallback={<PageLoader />}>
                <Visits />
              </Suspense>
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/visits/add"
        element={
          <ProtectedRoute>
            <Layout>
              <Suspense fallback={<PageLoader />}>
                <AddVisit />
              </Suspense>
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/visits/:id/edit"
        element={
          <ProtectedRoute>
            <Layout>
              <Suspense fallback={<PageLoader />}>
                <EditVisit />
              </Suspense>
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports"
        element={
          <ProtectedRoute>
            <Layout>
              <Suspense fallback={<PageLoader />}>
                <Reports />
              </Suspense>
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/payment-dues"
        element={
          <ProtectedRoute>
            <Layout>
              <Suspense fallback={<PageLoader />}>
                <PaymentDues />
              </Suspense>
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/payment-dues/monthly"
        element={
          <ProtectedRoute>
            <Layout>
              <Suspense fallback={<PageLoader />}>
                <MonthlyPaymentDues />
              </Suspense>
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <Layout>
              <Suspense fallback={<PageLoader />}>
                <Profile />
              </Suspense>
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/notifications"
        element={
          <ProtectedRoute requireSubscription={false}>
            <Layout>
              <Suspense fallback={<PageLoader />}>
                <Notifications />
              </Suspense>
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscription"
        element={
          <ProtectedRoute requireSubscription={false}>
            <Layout>
              <Suspense fallback={<PageLoader />}>
                <Subscription />
              </Suspense>
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/payment"
        element={
          <ProtectedRoute requireSubscription={false}>
            <Layout>
              <Suspense fallback={<PageLoader />}>
                <Payment />
              </Suspense>
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/help"
        element={
          <ProtectedRoute requireSubscription={false}>
            <Layout>
              <Suspense fallback={<PageLoader />}>
                <Help />
              </Suspense>
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/about"
        element={
          <ProtectedRoute requireSubscription={false}>
            <Layout>
              <Suspense fallback={<PageLoader />}>
                <About />
              </Suspense>
            </Layout>
          </ProtectedRoute>
        }
      />
      {/* Default redirect - redirect authenticated users to dashboard */}
      <Route 
        path="/dashboard-redirect" 
        element={<Navigate to="/dashboard" />} 
      />
    </Routes>
  );
}

function App() {
  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      // Ensure the webview does not underlap the status bar
      StatusBar.setOverlaysWebView({ overlay: false }).catch(() => {});
      // Optional styling for readability
      StatusBar.setStyle({ style: Style.Light }).catch(() => {});
      StatusBar.setBackgroundColor({ color: '#ffffff' }).catch(() => {});
      StatusBar.show().catch(() => {});

      // Add padding to body to account for status bar and navigation bar on Android
      const updateBodyPadding = () => {
        const safeAreaTop = getComputedStyle(document.documentElement)
          .getPropertyValue('env(safe-area-inset-top)') || '0px';
        const safeAreaBottom = getComputedStyle(document.documentElement)
          .getPropertyValue('env(safe-area-inset-bottom)') || '0px';
        document.body.style.paddingTop = `calc(${safeAreaTop} + 0px)`;
        document.body.style.paddingBottom = `calc(${safeAreaBottom} + 0px)`;
      };

      // Update on load and resize
      updateBodyPadding();
      window.addEventListener('resize', updateBodyPadding);

      // Permissions are now requested through the PermissionsOnboarding component
      // This ensures users understand why permissions are needed

      // Back button handling is now done in BackButtonHandler component inside Router

      // Stop voice recognition on app pause to avoid frozen UI from active mic sessions
      const pauseListener = CapacitorApp.addListener('pause', () => {
        const ev = new CustomEvent('meera:pause');
        window.dispatchEvent(ev);
      });
      const resumeListener = CapacitorApp.addListener('resume', () => {
        const ev = new CustomEvent('meera:resume');
        window.dispatchEvent(ev);
      });

      return () => {
        window.removeEventListener('resize', updateBodyPadding);
        if (pauseListener) pauseListener.remove();
        if (resumeListener) resumeListener.remove();
      };
    }
  }, []);

  return (
    <AuthProvider>
      <AdminAuthProvider>
        <SubscriptionProvider>
          <Router
            future={{
              v7_startTransition: true,
              v7_relativeSplatPath: true
            }}
          >
            <LoadingProvider>
              <NotificationBootstrap />
              <BackButtonHandler />
              <AppContent />
            </LoadingProvider>
          </Router>
        </SubscriptionProvider>
      </AdminAuthProvider>
    </AuthProvider>
  );
}

export default App;
